class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.dataset.noPageTransition = '';
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    if (!parsedState) return;

    if (!parsedState.sections) {
      parsedState = await this.fetchSections(parsedState);
    }

    const drawerInner = this.querySelector('.drawer__inner');
    drawerInner?.classList.contains('is-empty') && drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionHTML) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const nextHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (sectionElement && nextHTML != null) sectionElement.innerHTML = nextHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  async fetchSections(parsedState) {
    const sectionIds = this.getSectionsToRender().map((section) => section.id).join(',');
    const sectionsUrl = window.location.pathname;
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    try {
      const response = await fetch(`${cartUrl}?sections=${sectionIds}&sections_url=${encodeURIComponent(sectionsUrl)}`);

      if (!response.ok) return parsedState;

      const sections = await response.json();
      return {
        ...parsedState,
        sections,
      };
    } catch (error) {
      console.error('[Cart Drawer] Failed to fetch rendered cart sections:', error);
      return parsedState;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

console.log('[Cart Drawer] Checking if CartItems is available:', typeof CartItems);

// Wait for CartItems to be available if it's not loaded yet
if (typeof CartItems === 'undefined') {
  console.warn('[Cart Drawer] CartItems not defined yet, waiting...');
  // CartItems should be defined in cart.js, wait for it
  const checkCartItems = setInterval(() => {
    if (typeof CartItems !== 'undefined') {
      console.log('[Cart Drawer] CartItems now available');
      clearInterval(checkCartItems);
      defineCartDrawerItems();
    }
  }, 50);
  
  // Timeout after 5 seconds
  setTimeout(() => {
    if (typeof CartItems === 'undefined') {
      console.error('[Cart Drawer] CartItems still not available after 5 seconds!');
      clearInterval(checkCartItems);
    }
  }, 5000);
} else {
  defineCartDrawerItems();
}

function defineCartDrawerItems() {
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

console.log('[Cart Drawer] Registering cart-drawer-items custom element');
customElements.define('cart-drawer-items', CartDrawerItems);
console.log('[Cart Drawer] cart-drawer-items custom element registered');
}
